/**
 * Typed REST client. One source of truth for endpoint paths and request/
 * response shapes. Acquires a Microsoft Entra ID access token via MSAL and
 * attaches it as a bearer header on every call (see `./msal`). A dead session —
 * no token obtainable, or a 401 on a signed request — retires the session
 * (`endSession`); `AuthProvider` observes that and routes to the sign-in screen.
 *
 * API contract is the one in `docs/Tradeski-Plan-v1.md` §7.
 */
import { acquireApiToken, endSession } from './msal'
import { getActiveAccountId, reportAccountAccessLost } from './activeAccount'
import type { AccountAccessLostReason } from './activeAccount'

export type GameLink = {
    kalshiEventTicker: string
    homeKalshiMarketTicker: string
    awayKalshiMarketTicker: string
    yesSide: 'Home' | 'Away' | null
}

export type League = 'NBA' | 'WNBA' | 'NCAAM'

/** The kind of game ESPN files it under (docs/api/espn.md §10.1). */
export type SeasonType = 'Preseason' | 'RegularSeason' | 'PlayIn' | 'Postseason' | 'AllStar'

export type Game = {
    id: string
    espnEventId: string
    homeAbbreviation: string
    awayAbbreviation: string
    homeDisplayName: string
    awayDisplayName: string
    scheduledAt: string
    phase: 'Pregame' | 'InProgress' | 'Halftime' | 'Final' | 'Postponed'
    finalHome: number | null
    finalAway: number | null
    /** "NBA" / "WNBA" / "NCAAM". Drives league badge + per-league Kalshi
     *  placeholder + off-season banner. */
    league: League
    link: GameLink | null
    /** The system's background resolution of this game's Kalshi event —
     *  same shape as `link` with `yesSide` always null. Pre-populates the
     *  arm modal when the user has no link of their own; arming persists a
     *  real per-user link through the usual PUT /link validation. */
    autoLink: GameLink | null
    /** Null until a scoreboard pass reports it. */
    seasonType: SeasonType | null
}

/** `GET /api/games` response envelope. The `kalshiDirectoryStatus` field
 *  surfaces the backend's NCAAM-scoreboard pre-filter health so the UI can
 *  render a banner when Kalshi's events directory is unavailable. */
export type GameList = {
    games: Game[]
    kalshiDirectoryStatus: 'ok' | 'unavailable'
}

export type AutoLinkResponse = {
    link: GameLink
    resolvedFromTicker: string
    candidatesTried: string[]
}

export type SideSelection = 'Algorithm' | 'User'

export type Algorithm = {
    id: string
    name: string
    version: string
    parameterSchemaJson: string
    sideSelection: SideSelection
    supportedLeagues: League[]
    /** The kinds of game it runs on; null for every game, including one ESPN
     *  has not classified yet. */
    supportedSeasonTypes: SeasonType[] | null
    /** Whether its instances take Sell now. */
    supportsSellNow: boolean
    /** Kinds of game it also runs on, but only as a dry run: a configuration
     *  that stakes money is refused on them (`gameNotSupported`, reason
     *  `dryRunOnly`). Null for none. */
    dryRunSeasonTypes: SeasonType[] | null
}

/** `POST /api/algorithms/{name}/stake` — what a configuration would stake,
 *  computed by the algorithm itself. `stakeCents` is read leniently, so it is
 *  the stake the runtime would use even while `problems` says the
 *  configuration can't be armed. */
export type StakeQuote = {
    stakeCents: number
    confirmationRequired: boolean
    problems: string[]
}

export type Instance = {
    id: string
    userId: string
    gameId: string
    algorithmName: string
    configJson: string
    state: string
    createdAt: string
    finishedAt: string | null
}

/** `POST /api/instances` 409 body when the stake — the most the instance can
 *  spend, fees included, as the server reads the config — is at or above the
 *  confirmation threshold (admin setting, $100 by default) and the request
 *  didn't confirm exactly it. Re-send with `confirmedStakeCents: stakeCents`
 *  once the person accepts. */
export type StakeConfirmationRequired = {
    error: 'stakeConfirmationRequired'
    stakeCents: number
}

/**
 * One in-flight instance with everything the Active Wagers section needs:
 * the linked game's current state, the Kalshi market binding, the latest
 * orderbook snapshot (if any), and computed position + P&L. Backed by
 * `GET /api/instances/active` — see backend plan §1.1.
 */
export type ActiveInstance = {
    instanceId: string
    algorithmName: string
    state: string
    configJson: string
    createdAt: string
    game: ActiveGame
    link: GameLink
    /** The per-team ticker the instance actually trades (first traded ticker,
     *  else the user's chosen side, else home). Live `marketUpdated` pushes
     *  carry a ticker — only updates for THIS one may patch `market`, or the
     *  card price flips between the two complementary per-team markets. */
    markTicker: string
    market: ActiveMarket | null
    position: ActivePosition
    /** Server-computed money for the open position — the one set of figures
     *  the card, the detail modal, and the Sell now control all render, so
     *  none of them can derive a different number. Null when there is nothing
     *  to value: flat, unfilled, or no market data. */
    valuation: ActiveValuation | null
    /** The most the instance can spend, fees included, as its algorithm reads
     *  its configuration — what armed exposure sums. Null when the algorithm
     *  is no longer registered. */
    stakeCents: number | null
    /** Whether the instance's algorithm takes Sell now at all. */
    supportsSellNow: boolean
    /** The pregame prices a watching instance froze at the opening play;
     *  null before the freeze and for algorithms that freeze nothing. */
    pregameFreeze: PregameFreeze | null
}

/** Mids are cents, possibly on a half cent; null when that team's book had
 *  no two-sided quote at the tip. `favorite` is null when neither team was
 *  in the algorithm's pregame band. */
export type PregameFreeze = {
    tipAt: string
    homeMidCents: number | null
    awayMidCents: number | null
    favorite: 'Home' | 'Away' | null
}

export type ActiveGame = {
    id: string
    espnEventId: string
    homeAbbreviation: string
    awayAbbreviation: string
    homeDisplayName: string
    awayDisplayName: string
    scheduledAt: string
    phase: 'Pregame' | 'InProgress' | 'Halftime' | 'Final' | 'Postponed'
    finalHome: number | null
    finalAway: number | null
    league: League
}

export type ActiveMarket = {
    bestBidCents: number | null
    bestAskCents: number | null
    capturedAt: string
}

/** `GET /api/instances/{id}/market-history` — recent best-bid/ask snapshots
 *  for the instance's mark ticker, oldest first, downsampled server-side.
 *  Seeds the price chart on page load; live hub pushes append from there.
 *  `trades` marks where this instance's own fills landed on the same axes. */
export type MarketHistory = {
    ticker: string
    points: MarketHistoryPoint[]
    trades: MarketTradeMarker[]
}

export type MarketHistoryPoint = {
    at: string
    bestBidCents: number | null
    bestAskCents: number | null
}

/** One fill projected onto a price chart: fill time × YES price. Ledger rows
 *  sharing an order and a price arrive collapsed into one mark, stamped at the
 *  earliest fill in the group. Not a money figure — P&L comes from the fold. */
export type MarketTradeMarker = {
    at: string
    side: 'Buy' | 'Sell'
    priceCents: number
    contracts: number
    ticker: string
}

/** `GET /api/games/{id}/market-history` — both per-team books across a whole
 *  game with the game's own timeline beside them; the finished-game review.
 *  `unavailable` is null when there is a book to chart, and otherwise says
 *  why there isn't. */
export type GameMarketHistory = {
    gameId: string
    eventTicker: string | null
    from: string
    to: string
    retainedFrom: string
    unavailable: GameMarketUnavailable | null
    sides: GameMarketSide[]
    markers: GameTimelineMarker[]
    scores: GameScorePoint[]
    trades: MarketTradeMarker[]
}

export type GameMarketUnavailable = 'notLinked' | 'notRecorded' | 'pruned'

export type GameMarketSide = {
    side: 'Home' | 'Away'
    abbreviation: string
    ticker: string
    points: MarketHistoryPoint[]
}

/** `kind` is the event_log type (`TipOff` / `PeriodEnded` /
 *  `HalftimeStarted` / `GameFinal`); `label` is its short chart tick. */
export type GameTimelineMarker = {
    at: string
    kind: string
    label: string
}

export type GameScorePoint = {
    at: string
    home: number
    away: number
}

export type ActivePosition = {
    contracts: number
    averageEntryCents: number | null
    realizedPnlCents: number
    unrealizedPnlCents: number | null
}

/** What the open position is worth at `markBidCents`, and what selling it
 *  there would return. `markValueCents`/`changeCents` are the fee-free
 *  mark-to-market view the rest of the product's in-flight P&L uses;
 *  `netProceedsCents`/`netChangeCents` deduct the estimated Kalshi trading
 *  fee, which is what a sale actually returns. Every figure is integer cents. */
export type ActiveValuation = {
    contracts: number
    markBidCents: number
    costCents: number
    markValueCents: number
    changeCents: number
    exitFeeCents: number
    netProceedsCents: number
    netChangeCents: number
}

/** Where one game of tonight's slate stands for the account. */
export type SlateGameStatus =
    | 'eligible'
    | 'alreadyArmed'
    | 'notLinked'
    | 'leagueNotSupported'
    | 'seasonTypeNotSupported'
    | 'seasonTypeUnknown'

/** `GET /api/instances/slate?algorithm=` — the not-yet-started games of the
 *  algorithm's leagues over the next day. */
export type Slate = {
    algorithmName: string
    games: SlateGame[]
}

export type SlateGame = {
    gameId: string
    league: League
    homeAbbreviation: string
    awayAbbreviation: string
    homeDisplayName: string
    awayDisplayName: string
    scheduledAt: string
    seasonType: SeasonType | null
    status: SlateGameStatus
    /** The live instance already armed on the game, and its algorithm. */
    instanceId: string | null
    instanceAlgorithmName: string | null
    /** The account has no link of its own; arming binds the system's
     *  resolution of the game's markets. */
    adoptsSystemLink: boolean
    /** The game's kind takes the algorithm only as a dry run: `eligible` for
     *  a configuration that stakes nothing, refused (`dryRunOnly`) for any
     *  other. */
    dryRunOnly: boolean
}

export type SlateArmStatus =
    | 'armed'
    | 'alreadyArmed'
    | 'notLinked'
    | 'notSupported'
    | 'dryRunOnly'
    | 'notInSlate'
    | 'confirmationRequired'

/** `POST /api/instances/slate` — every chosen game's outcome. */
export type ArmSlateResponse = {
    algorithmName: string
    stakeCents: number
    games: { gameId: string; status: SlateArmStatus; instanceId: string | null }[]
}

/** `POST /api/instances/slate` 409: the per-game stake needs confirming
 *  before any game arms. `games` is how many were chosen; the confirmation
 *  repeats both numbers, since it covers their product. */
export type SlateStakeConfirmationRequired = StakeConfirmationRequired & { games: number }

/** `POST /api/instances/{id}/sell-now`. `status` is `placed` or
 *  `alreadyCovered` on 200; on 409 it names the refusal — `notAttached`,
 *  `notSupported`, `noPosition`, `noLiveMarket`, `marketUnreadable`,
 *  `priceMoved`, `rejected`, `orderOutcomeUnknown` (an order of the instance
 *  reached Kalshi without an answer, so no other sell goes out until it is
 *  accounted for) — and `bestBidCents` is what the market said at that
 *  moment, so a `priceMoved` retry can be made against it. */
export type SellNowResponse = {
    status: string
    contracts: number
    exitCents: number | null
    bestBidCents: number | null
    minAcceptableCents: number
}

export type CredentialsStatus = {
    hasCredentials: boolean
    revoked: boolean
    updatedAt: string | null
}

/**
 * Live Kalshi cash balance for the signed-in user. Fetched on demand via
 * `GET /api/user/me/balance`. The backend always returns 200 with a
 * discriminated payload so widgets render every state cleanly:
 *   - `hasCredentials: false` → user hasn't linked Kalshi
 *   - `error: "upstream"`     → balance fetch failed (rate-limit, network, etc.)
 *   - otherwise               → `balanceCents` is current cash in cents
 */
export type UserBalance = {
    hasCredentials: boolean
    balanceCents: number | null
    fetchedAt: string | null
    error: string | null
}

export type AccountKind = 'Person' | 'Organization'

/** One entry of the account switcher. `displayName` falls back to the email
 *  for people, who carry no display name today. */
export type AccountSummary = {
    id: string
    displayName: string
    kind: AccountKind
    isSelf: boolean
}

/**
 * The signed-in PERSON — never the account a request acts for — plus every
 * account they may switch to: their own row first, then granted organizations
 * by name (ai_docs/corporate-user-feature.md §7.1).
 */
export type UserMe = {
    id: string
    email: string
    displayName: string | null
    kind: AccountKind
    roles: string[]
    commissionRate: number | null
    createdAt: string
    accounts: AccountSummary[]
}

export type DashboardLayout = { layoutJson: string; source: 'default' | 'override' }

export type DashboardPnl = {
    todayCents: number
    weekCents: number
    allTimeCents: number
    winCount: number
    lossCount: number
    winRate: number
}

export type RecentTrade = {
    id: string
    instanceId: string
    ticker: string
    side: string
    cents: number
    contracts: number
    status: string
    placedAt: string
    filledAt: string | null
    fillCents: number | null
    filledContracts: number | null
}

export type DashboardSummary = {
    pnl: DashboardPnl
    activeInstanceCount: number
    liveGameCount: number
    upcomingGameCount: number
    recentTrades: RecentTrade[]
}

export type UserPreferences = { dashboardLayoutJson: string | null; updatedAt: string }

/** One person granted access to an organization. */
export type AccountGrantee = { userId: string; email: string; grantedAt: string }

export type AdminUserSummary = {
    id: string
    email: string
    displayName: string | null
    kind: AccountKind
    roles: string[]
    commissionRate: number | null
    createdAt: string
    hasCredentials: boolean
    credentialsRevoked: boolean
    /** People granted access to this organization; empty for people. */
    grantees: AccountGrantee[]
}

/** `POST /api/admin/users`: a person signs in with Microsoft and carries a
 *  role, so their email is required; an organization never signs in, needs a
 *  display name, may not carry a role (the server answers `orgCannotHaveRole`),
 *  and takes an optional contact mailbox — send `null` and it has none. */
export type CreateAccountRequest =
    | { kind: 'Person'; email: string; role: 'User' | 'Admin'; commissionRate: number | null }
    | {
          kind: 'Organization'
          email: string | null
          displayName: string
          commissionRate: number | null
      }

export type CommissionOutcome = 'Settled' | 'NoTrade' | 'Cancelled' | 'Errored'

/** Compact game summary carried on a commission row + its report — the actual
 *  matchup the instance traded, joined server-side via the instance. */
export type CommissionGame = {
    id: string
    espnEventId: string
    homeAbbreviation: string
    awayAbbreviation: string
    homeDisplayName: string
    awayDisplayName: string
    scheduledAt: string
    phase: 'Pregame' | 'InProgress' | 'Halftime' | 'Final' | 'Postponed'
    finalHome: number | null
    finalAway: number | null
    league: League
}

// One row per resolved instance — the ledger is complete, so armed instances
// that never traded appear too. Status 'None' = zero-amount row (no charge).
export type CommissionEntry = {
    id: string
    userId: string
    userEmail: string
    /** Set for organization rows ("Cloud Compass LLC"); null for people. */
    userDisplayName: string | null
    instanceId: string
    tradeOrderId: string | null
    outcome: CommissionOutcome
    profitCents: number
    /** Σ manual adjustments (ticket #22). The user-facing net is
     *  profitCents + adjustmentCents; profitCents stays the instance's own
     *  fill-derived number. */
    adjustmentCents: number
    rate: number
    amountCents: number
    status: 'Pending' | 'Paid' | 'Waived' | 'None'
    createdAt: string
    paidAt: string | null
    paidByUserId: string | null
    notes: string | null
    /** pdx-conn §7.4: the user traded this market outside Tradeski during the
     *  instance's life, so account cash won't reconcile against this row's
     *  instance-scoped net — the cue that a manual adjustment may be warranted. */
    externalActivityDetected: boolean
    /** Null only when the instance or game row is missing (deleted). */
    game: CommissionGame | null
}

export type CommissionInstance = {
    id: string
    algorithmName: string
    algorithmVersion: string
    configJson: string
    state: string
    createdAt: string
    finishedAt: string | null
}

/** One `algorithm_decisions` row — the algorithm's own log of what it did. */
export type CommissionDecision = {
    id: number
    at: string
    triggeringEventId: number | null
    decisionType: string
    payloadJson: string
}

/** One `trade_orders` row for the instance, in placement order. */
export type CommissionOrder = {
    id: string
    ticker: string
    side: string
    cents: number
    contracts: number
    kalshiOrderId: string | null
    status: string
    placedAt: string
    filledAt: string | null
    fillCents: number | null
    filledContracts: number | null
}

/** One manual adjustment on a commission row (ticket #22), oldest first. */
export type CommissionAdjustment = {
    id: string
    deltaCents: number
    reason: string
    createdByUserId: string
    createdByEmail: string | null
    createdAt: string
}

/** Full drill-down for one ledger row — the commission + game, the resolved
 *  instance, the decision timeline, the trade orders, the manual adjustments,
 *  and every audit_log row referencing this instance/commission. Backs the
 *  report modal + PDF. */
export type CommissionReport = {
    entry: CommissionEntry
    instance: CommissionInstance | null
    decisions: CommissionDecision[]
    orders: CommissionOrder[]
    adjustments: CommissionAdjustment[]
    audit: AuditEntry[]
}

export type AuditEntry = {
    id: number
    actorUserId: string | null
    actorEmail: string | null
    /** The organization the actor acted for; null when they acted for
     *  themself (or the row is system-written). */
    onBehalfOfUserId: string | null
    onBehalfOfDisplayName: string | null
    action: string
    target: string
    payloadJson: string
    at: string
}

/** Registry definition + persisted value for one system setting (ticket #24).
 *  `kind` drives the editor: number input, select, or JSON textarea. */
export type SystemSetting = {
    key: string
    category: string
    label: string
    description: string
    kind: 'integer' | 'decimal' | 'choice' | 'json'
    valueJson: string
    defaultJson: string
    min: number | null
    max: number | null
    choices: string[] | null
    updatedAt: string | null
    updatedBy: string | null
}

// Phase 4: recordings + replay
export type RecordingRequest = {
    id: string
    gameId: string
    requestedByUserId: string | null
    reason: 'InstanceArmed' | 'UserAnalysis' | 'AdminAlwaysRecord' | 'AutoAnalysis'
    state: 'Requested' | 'Active' | 'Stopped' | 'Failed'
    requestedAt: string
    startedAt: string | null
    stoppedAt: string | null
    notes: string | null
}

export type Recording = {
    gameId: string
    espnEventId: string
    homeTeam: string
    awayTeam: string
    eventLogRowCount: number
    marketSnapshotRowCount: number
    marketSnapshotRowCountIsEstimate: boolean
    firstEventAt: string | null
    lastEventAt: string | null
    requests: RecordingRequest[]
}

export type RecordingImportResult = {
    gameId: string | null
    espnEventId: string
    status: 'Imported' | 'SkippedDuplicate' | 'Failed'
    eventRowsWritten: number
    snapshotRowsWritten: number
    detail: string | null
}

export type PagedRecordings = {
    items: Recording[]
    totalCount: number
    page: number
    pageSize: number
}

export type ReplaySpeed = '1x' | '4x' | '16x' | 'Instant'

export type ReplaySession = {
    id: string
    gameId: string
    userId: string
    algorithmName: string
    configJson: string
    speed: ReplaySpeed
    status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Cancelled'
    startedAt: string
    completedAt: string | null
    eventsProcessed: number
    decisions: number
    ordersPlaced: number
    realizedPnlCents: number | null
    failureReason: string | null
}

export type BacktestResult = {
    gameId: string
    espnEventId: string
    sessionId: string | null
    status: string
    eventsProcessed: number
    decisions: number
    ordersPlaced: number
    realizedPnlCents: number | null
    failureReason: string | null
}

/**
 * Queued backtest job: POST returns 202 with status Queued/Running; poll
 * `replayGetBacktest` until Completed/Failed. `results` accumulates per game
 * in submission order as the worker progresses.
 */
export type BacktestJob = {
    id: string
    userId: string
    algorithmName: string
    status: 'Queued' | 'Running' | 'Completed' | 'Failed'
    createdAt: string
    startedAt: string | null
    completedAt: string | null
    totalGames: number
    completedGames: number
    results: BacktestResult[]
    failureReason: string | null
}

export type ReplayDecisionType =
    | 'QualifyEntry'
    | 'SkipEntry'
    | 'PlaceOrder'
    | 'CancelOrder'
    | 'Transition'
    | 'Note'
    | 'QualifyTopUp'

export type ReplayDecision = {
    at: string
    triggeringEventSequence: number | null
    type: ReplayDecisionType
    payloadJson: string
}

export type ReplayOrderSide = 'Buy' | 'SellReduceOnly' | 'Cancel'

export type ReplayOrder = {
    at: string
    ticker: string
    side: ReplayOrderSide
    /** Yes/No side the algorithm declared. Null for cancel rows (cancel-by-id
     *  has no side). */
    intentSide: 'Yes' | 'No' | null
    limitCents: number
    contracts: number
    decisionId: string
    kalshiOrderId: string
    status: string
    fillCents: number | null
    filledContracts: number
    realizedDeltaCents: number | null
    rejectReason: string | null
}

export type ReplayOutcomeDetail = {
    yesSide: 'Home' | 'Away' | null
    finalHomeScore: number | null
    finalAwayScore: number | null
    yesWon: boolean | null
    heldContracts: number
    averageEntryCents: number
    entryCostCents: number
    realizedPnlCents: number
    settlementPnlCents: number | null
    totalPnlCents: number | null
    gameSettled: boolean
}

export type ReplaySessionDetail = {
    session: ReplaySession
    decisions: ReplayDecision[]
    orders: ReplayOrder[]
    outcome: ReplayOutcomeDetail | null
}

// History (ai_docs/history-section.md). A "trade" is one
// algorithm instance that actually attempted entry (≥1 Buy order).

/** Fine-grained derived status of a History row (server enum names). */
export type HistoryTradeStatus =
    | 'Pending'
    | 'Submitted'
    | 'PartiallyFilled'
    | 'Open'
    | 'TargetSellSubmitted'
    /** A Sell now's exit order rests — a person overrode the strategy's exit. */
    | 'SellNowSubmitted'
    | 'Sold'
    | 'SoldEarly'
    | 'Won'
    | 'Lost'
    | 'Rejected'
    | 'Cancelled'
    | 'Failed'

export type HistoryExitReason =
    'TargetReached' | 'LateGameEscape' | 'BreakevenExit' | 'Settlement' | 'Cancelled' | 'SellNow'

/** The filter bar's coarse status values — the server maps them onto the fine
 *  statuses (`Open` ⇒ Pending/Submitted/Open/TargetSellSubmitted/
 *  SellNowSubmitted, `Failed` ⇒ Failed/Rejected, others 1:1). */
export type HistoryStatusFilter =
    'Open' | 'PartiallyFilled' | 'Sold' | 'Won' | 'Lost' | 'SoldEarly' | 'Cancelled' | 'Failed'

/** Server-side filters shared by both History endpoints. `from`/`to` are ISO
 *  instants ([from, to) — `to` exclusive); the viewer tz is attached
 *  automatically for day bucketing. */
export type HistoryQuery = {
    from?: string
    to?: string
    /** Algorithm names; empty/omitted = all. */
    algorithms?: string[]
    status?: HistoryStatusFilter
    league?: League
}

/** One instance-as-trade row. Money is integer cents; null = "not derivable
 *  from the record" and renders "—", never a fabricated 0. */
export type HistoryTrade = {
    instanceId: string
    gameId: string
    league: League
    homeAbbreviation: string
    awayAbbreviation: string
    homeDisplayName: string
    awayDisplayName: string
    gameStartAt: string
    gamePhase: string
    finalHome: number | null
    finalAway: number | null
    eventTicker: string | null
    marketTicker: string | null
    selectedSide: 'Home' | 'Away' | null
    selectedTeamAbbreviation: string | null
    algorithmName: string
    algorithmVersion: string
    status: HistoryTradeStatus
    exitReason: HistoryExitReason | null
    entryAt: string
    maxBetCents: number | null
    targetMultiplier: number | null
    investedCents: number
    averageEntryCents: number | null
    requestedContracts: number
    boughtContracts: number
    soldContracts: number
    heldContracts: number
    targetCents: number | null
    averageSellCents: number | null
    sellProceedsCents: number
    settlementValueCents: number | null
    /** Latest best bid for the traded ticker — open rows only. */
    currentMarketBidCents: number | null
    feeCents: number | null
    realizedPnlCents: number | null
    unrealizedPnlCents: number | null
    externalActivityDetected: boolean
    createdAt: string
    finishedAt: string | null
    /** The person who armed this trade for an organization; null for a
     *  personal trade (armed by its owner). */
    armedByEmail: string | null
}

/** Always "today" in the viewer's zone regardless of the date-range filter
 *  (D2); algorithm/status/market filters still apply. */
export type HistoryToday = { tradeCount: number; investedCents: number }

/** Open positions within the current filters; atRiskCents = Σ held × avg entry. */
export type HistoryOpen = { tradeCount: number; atRiskCents: number }

export type HistoryAlgorithmSummary = {
    name: string
    version: string
    trades: number
    completed: number
    open: number
    wins: number
    maxBetCents: number
    investedCents: number
    realizedPnlCents: number
    unrealizedPnlCents: number
    /** Contract-weighted, sub-cent precision (e.g. 14.6). */
    averageEntryCents: number | null
}

export type HistoryDaySummary = {
    /** Viewer-local calendar date, `YYYY-MM-DD`. */
    date: string
    trades: number
    investedCents: number
    wins: number
    completed: number
    pnlCents: number
}

/** Everything aggregate for the History page in one payload — each number is
 *  a straight sum of the row values `/api/history/trades` serves, so cards,
 *  the per-algorithm table, and day headers can never disagree. Win rate and
 *  ROI are computed client-side (`wins/completed`, `realized/invested`) so
 *  division-by-zero renders "—" in one visible place. */
export type HistorySummary = {
    realizedPnlCents: number
    unrealizedPnlCents: number
    totalPnlCents: number
    wins: number
    completedTrades: number
    tradeCount: number
    investedCents: number
    weightedAverageEntryCents: number | null
    today: HistoryToday
    open: HistoryOpen
    algorithms: HistoryAlgorithmSummary[]
    days: HistoryDaySummary[]
}

/** The settled score at the entry trigger (D11 event-log probe) — for
 *  HalftimeSwing this is the halftime score. */
export type HistoryEntryScore = { home: number; away: number }

/** One trade_order_fills ledger row (exactly-once by exchange fill id). */
export type HistoryOrderFill = {
    fillId: string
    contracts: number
    yesPriceCents: number
    /** Server-reported fee; null until the REST sweep backfills it. */
    feeCostDollars: number | null
    isTaker: boolean
    source: string
    filledAt: string
}

/** One trade_orders row with its fill ledger. `fills` is empty for Cancel
 *  rows and pre-ledger history (fillCents/filledContracts still aggregate). */
export type HistoryDetailOrder = {
    id: string
    ticker: string
    side: 'Buy' | 'SellReduceOnly' | 'Cancel'
    limitCents: number
    contracts: number
    kalshiOrderId: string | null
    status: string
    placedAt: string
    filledAt: string | null
    fillCents: number | null
    filledContracts: number | null
    fills: HistoryOrderFill[]
}

/** One algorithm_decisions row; `triggeringEventId` is the game-event
 *  sequence that caused it. */
export type HistoryDecision = {
    id: number
    at: string
    triggeringEventId: number | null
    decisionType: string
    payloadJson: string
}

/** The instance's commission resolution + SettlementPoller Notes breakdown. */
export type HistoryCommissionBreakdown = {
    outcome: string
    profitCents: number
    /** Σ manual adjustments (ticket #22); the trade's realizedPnlCents
     *  already includes it — surfaced here so the detail can show the split. */
    adjustmentCents: number
    marketResult: string | null
    settlementValueCents: number | null
    feeCents: number | null
}

/** Full record of one trade (`GET /api/history/trades/{instanceId}`). The
 *  JSON passthroughs are verbatim server snapshots — the client renders any
 *  algorithm's vocabulary generically. */
export type HistoryTradeDetail = {
    trade: HistoryTrade
    configJson: string
    /** QualifyEntry decision payload, verbatim; null when never logged. */
    entryConditionsJson: string | null
    /** When the algorithm decided to enter — distinct from `trade.entryAt`
     *  (the entry order's placement). */
    triggerAt: string | null
    scoreAtEntry: HistoryEntryScore | null
    /** KalshiFees formula re-run over the actual fills, vs the actual
     *  server-reported `trade.feeCents`. */
    estimatedFeeCents: number | null
    orders: HistoryDetailOrder[]
    decisions: HistoryDecision[]
    commission: HistoryCommissionBreakdown | null
}

/* ── Predictive engine (shadow analyses — plan §10) ─────────────────── */

export type EngineAction = 'Enter' | 'Watch' | 'Skip'

export type EngineBlockerCategory = 'Data' | 'Liquidity' | 'Statistical' | 'OptionalValue' | 'Risk'

export type EngineBlocker = {
    code: string
    category: EngineBlockerCategory
    detail: string
}

/** The strongest ranked candidate, flattened server-side for list cards. */
export type EngineBestCandidate = {
    policyId: string
    family: 'Baseline' | 'ExitOdds' | 'Reversion'
    entryPriceCents: number
    targetPriceCents: number
    stopPriceCents: number
    horizonSeconds: number
    action: EngineAction
    conservativeEvPerContractCents: number
    fillProbabilityLower: number
    targetProbabilityLower: number
    effectiveSampleSize: number
    rawNeighborCount: number
    blockerCount: number
}

/** `GET /api/engine/analyses` row — one halftime evaluation of one side. */
export type EngineAnalysisSummary = {
    analysisId: string
    gameId: string
    league: League
    ticker: string
    teamId: string
    action: EngineAction
    evaluatedAt: string
    createdAt: string
    profileVersion: string
    homeAbbreviation: string | null
    awayAbbreviation: string | null
    /** Every generated candidate (the persisted grid), not just ranked ones. */
    candidateCount: number
    globalBlockerCount: number
    /** Null when the side generated no candidates (typical for the leader). */
    bestCandidate: EngineBestCandidate | null
}

export type EngineCandidate = {
    policy: {
        policyId: string
        family: 'Baseline' | 'ExitOdds' | 'Reversion'
        entryPriceCents: number
        targetPriceCents: number
        stopPriceCents: number
        horizonSeconds: number
    }
    estimate: {
        rawNeighborCount: number
        effectiveSampleSize: number
        effectiveSampleSizeFilled: number
        fillProbability: number
        fillProbabilityLower: number
        targetProbability: number
        targetProbabilityLower: number
        stopProbability: number
        stopProbabilityUpper: number
        timeoutProbability: number
        timeoutPriceCents: number
        timeoutPriceCentsConservative: number
    }
    economics: {
        contracts: number
        entryCostCents: number
        entryFeeCents: number
        targetPnlCents: number
        stopPnlCents: number
        timeoutPnlCents: number
        timeoutPnlCentsConservative: number
        conditionalEvCents: number
        expectedEvCents: number
        conservativeEvCents: number
        conservativeEvPerContractCents: number
        breakEvenTargetProbability: number
    }
    blockers: EngineBlocker[]
    waivedBlockerCodes: string[]
    matchedRuleIds: string[]
    action: EngineAction
}

export type EngineFairValueContribution = {
    feature: string
    logOdds: number
    plainLanguage: string
}

export type EngineFairValue = {
    pregameProbabilityUsed: number
    pregameProbabilityWasUnknown: boolean
    contributions: EngineFairValueContribution[]
    fairValueProbability: number
}

/** The pinned `EngineAnalysisJson` document persisted by the evaluator. */
export type EngineAnalysisResultDocument = {
    analysisId: string
    action: EngineAction
    candidates: EngineCandidate[]
    allCandidates: EngineCandidate[]
    globalBlockers: EngineBlocker[]
    fairValue: EngineFairValue
    profileVersion: string
    profileFingerprint: string
}

/** `GET /api/engine/analyses/{id}` — header metadata + the full document. */
export type EngineAnalysisDetail = {
    analysisId: string
    gameId: string
    league: League
    ticker: string
    teamId: string
    action: EngineAction
    evaluatedAt: string
    createdAt: string
    profileVersion: string
    profileFingerprint: string
    homeAbbreviation: string | null
    awayAbbreviation: string | null
    result: EngineAnalysisResultDocument
}

export type EngineStatus = {
    profiles: {
        league: League
        version: string
        fingerprint: string
        entryLatencyMs: number
        intendedContracts: number
    }[]
    dataset: {
        signalsByStatus: Record<string, number>
        observationsByLeague: Record<string, number>
        observationCount: number
        analysisCount: number
        pendingSignalGames: number
        labelableGames: number
    }
    latency: {
        decisionToDispatchMs: number | null
        dispatchToFillMs: number | null
        entryLatencyMs: number | null
        sql: string
    }
    bookCache: {
        ticker: string
        capturedAt: string
        midHistoryPoints: number
        bookIncoherent: boolean
    }[]
}

export type EngineObservation = {
    observationId: string
    signalId: string
    gameId: string
    league: League
    outcome: 'Target' | 'Stop' | 'Timeout' | 'Unfilled'
    entryPriceCents: number
    actualEntryPriceCents: number | null
    entryDelayMs: number
    outcomeElapsedSeconds: number
    labelAvailableAtMs: number
    pnlCents: number
    feeCents: number
    dataCompleteness: number
    targetMoveCents: number
    stopMoveCents: number
    horizonSeconds: number
    profileVersion: string
    createdAt: string
}

export type EngineRuleStatus = 'Candidate' | 'Approved' | 'Retired'

export type EngineRule = {
    ruleId: string
    league: League
    conditions: { feature: string; min: number; max: number }[]
    discoveryStats: Record<string, unknown>
    validationStats: Record<string, unknown>
    status: EngineRuleStatus
    minedAt: string
    reviewedByUserId: string | null
    reviewedAt: string | null
    notes: string | null
}

/** One rule-mining job snapshot. `candidatesInserted` below `candidatesMined`
 *  means the rest already existed — re-mining is idempotent by deterministic
 *  RuleId and never disturbs review state (a retired rule stays retired). */
export type EngineMiningJob = {
    id: string
    league: League
    requestedByUserId: string
    status: 'Queued' | 'Running' | 'Completed' | 'Failed'
    createdAt: string
    startedAt: string | null
    completedAt: string | null
    observationsScanned: number
    candidatesMined: number
    candidatesInserted: number
    failureReason: string | null
}

/** One backfill job snapshot; re-runs legitimately report zeros everywhere
 *  (every write is idempotent by deterministic ID). */
export type EngineBackfillJob = {
    id: string
    gameId: string
    requestedByUserId: string
    status: 'Queued' | 'Running' | 'Completed' | 'Failed'
    createdAt: string
    startedAt: string | null
    completedAt: string | null
    signalsInserted: number
    signalsLabeled: number
    signalsDiscarded: number
    signalsFailed: number
    observationsInserted: number
    failureReason: string | null
}

/** One rejected NDJSON line of the corpus import (1-based line number +
 *  machine reason code, surfaced verbatim). */
export type EngineObservationImportReject = {
    line: number
    reason: string
}

/** `POST /api/admin/engine/observations/import` result. On a dry run
 *  `inserted` is null and nothing was written. ObservationIds embed the
 *  profile fingerprint and simulated entry (corpus doc §2.8), so a corrected
 *  corpus produces different ids that would COEXIST with a previous import —
 *  nonzero `wouldInsert` beside nonzero `alreadyPresent` is the §6.10
 *  two-variant tripwire. `rejected` is capped at 100 entries;
 *  `rejectedCount` is the true total. */
export type EngineObservationImportReport = {
    dryRun: boolean
    total: number
    wouldInsert: number
    alreadyPresent: number
    inserted: number | null
    rejectedCount: number
    rejected: EngineObservationImportReject[]
}

/* ── Engine metrics (engine-metrics-dashboard plan §6.1) ────────────── */

/** Filter for the engine metrics endpoints. `league` omitted or 'all' scopes
 *  to every league; `from`/`to` are ISO instants — omitted, the server
 *  defaults to the last 30 viewer-local days and rejects spans over 90 days
 *  (`rangeTooWide`). */
export type EngineMetricsQuery = {
    league?: League | 'all'
    from?: string
    to?: string
}

/** One viewer-local day of analysis counts (`day` = 'YYYY-MM-DD'), ascending
 *  and sparse — days without analyses are absent. Bucketed on the evaluation
 *  moment, never row-landing time (backfill lands historical evaluations
 *  "now"). */
export type EngineActionMixDay = {
    day: string
    enter: number
    watch: number
    skip: number
}

/** One open/acknowledged alert of an engine-relevant kind; ack/resolve lives
 *  on the admin page. */
export type EngineMetricsAlert = {
    id: number
    kind: string
    severity: 'Info' | 'Warning' | 'Error'
    state: 'Open' | 'Acknowledged'
    target: string | null
    message: string
    occurrenceCount: number
    firstSeenAt: string
    lastSeenAt: string
}

/** `GET /api/admin/engine/metrics/overview`. Mode, profiles, alerts, table
 *  counts, and the backlog are current-state; the funnel / action-mix /
 *  dataset blocks scope to the league + date window. */
export type EngineMetricsOverview = {
    mode: string
    profiles: EngineStatus['profiles']
    funnel: {
        slateGames: number
        /** Distinct games with an analysis inside the window — not clamped to
         *  the slate, so an edge-of-window halftime can exceed `slateGames`. */
        evaluatedGames: number
        sidesWithCandidates: number
        analysesByAction: Record<EngineAction, number>
        /** Best-effort reasons (historical eligibility can't be perfectly
         *  reconstructed); the unclassified remainder is implicit. */
        unevaluated: {
            noProfileLeague: number
            notRecorded: number
            postponed: number
        }
    }
    actionMixByDay: EngineActionMixDay[]
    dataset: {
        observationsByLeagueAndOutcome: Record<string, Record<string, number>>
        discardsByReason: Record<string, number>
        meanCompleteness: number | null
        signalsByStatus: Record<string, number>
        backlog: { pendingSignalGames: number; labelableGames: number }
    }
    alerts: EngineMetricsAlert[]
    tableCounts: {
        engineSignals: number
        labeledObservations: number
        engineAnalyses: number
        engineDiscoveredRules: number
        auditLog: number
        tradeOrderFills: number
    }
}

/** `GET /api/admin/engine/metrics/blockers` — the blocker Pareto. Candidate
 *  blockers count blocked candidates; a global blocker (`sideMismatch`)
 *  counts its analyses. Codes are verbatim engine codes — map to friendly
 *  labels at the rendering edge only. */
export type EngineBlockerMetrics = {
    totalCandidates: number
    blockers: {
        code: string
        category: EngineBlockerCategory
        count: number
        /** count / totalCandidates, 4 dp; 0 when the window generated no
         *  candidates. */
        share: number
    }[]
}

/** One viewer-local day of simulated P/L (`day` = 'YYYY-MM-DD'), ascending
 *  and sparse. `enterPnlCents` ⊆ `allPnlCents`; `count` spans all actions. */
export type EngineLedgerDay = {
    day: string
    allPnlCents: number
    enterPnlCents: number
    count: number
}

/** Which conservative prediction `GET metrics/calibration` buckets: `target`
 *  (P(target | fill) — realized over filled observations only) or `fill`
 *  (realized over every observation). */
export type EngineCalibrationMetric = 'target' | 'fill'

/** One predicted-probability bucket, [lo, hi) — the last includes 1. `n` is
 *  both the sample count and the realized-rate denominator; mean/rate are
 *  4 dp and null on an empty bucket. */
export type EngineCalibrationBucket = {
    lo: number
    hi: number
    predictedMean: number | null
    realizedRate: number | null
    n: number
}

/** `GET /api/admin/engine/metrics/calibration` — the reliability buckets
 *  (spec §20 "calibration by probability bucket"). Ten dense tenth-wide
 *  buckets, always all present; realized rates are SIMULATED triple-barrier
 *  outcomes. Observations whose signal → analysis → candidate join breaks
 *  degrade out entirely. */
export type EngineCalibrationMetrics = {
    buckets: EngineCalibrationBucket[]
}

/** `GET /api/admin/engine/metrics/ledger` — the shadow ledger. Every figure
 *  is SIMULATED shadow P/L (latency-adjusted, full-fill-or-discard
 *  triple-barrier, net of simulated fees) — never real money, never summed
 *  with it. Rows window and bucket on the observation's label-available
 *  moment, honest under backfill. */
export type EngineLedgerMetrics = {
    series: EngineLedgerDay[]
    totals: {
        simulatedNetCents: number
        /** Filled (outcome != Unfilled) / all observations, 4 dp; 0 on an
         *  empty window. */
        fillRate: number
        /** Target outcomes / filled observations, 4 dp; 0 when nothing
         *  filled. */
        targetRateAmongFilled: number
        /** Largest peak-to-trough decline of the cumulative curve — a
         *  magnitude, ≥ 0. */
        maxDrawdownCents: number
        /** Distinct games contributing observations (spec §20 "adequate
         *  distinct games"). */
        distinctGames: number
        /** Filled observations — each a simulated FULL fill at intended
         *  quantity (the simulation is full-fill-or-discard). */
        filledObservations: number
        /** The drawdown fold restricted to ENTER observations — the
         *  promotable scenario's own curve; 0 without joined ENTER rows. */
        enterMaxDrawdownCents: number
        /** Largest game's |ENTER net| ÷ Σ per-game |ENTER net|, 4 dp; null
         *  without joined ENTER rows or when every per-game net is zero. */
        enterLargestGameShare: number | null
    }
    /** Always all three action keys. Observations whose signal → analysis →
     *  candidate join breaks degrade out of the slices but stay in every
     *  `all` figure — the remainder is totals minus the slice sums. */
    byAction: Record<EngineAction, { count: number; netCents: number }>
}

/** One viewer-local day of latency medians, bucketed on order placement,
 *  ascending and sparse. Null medians = no qualifying pairs; `orders` is the
 *  day's entry-order count (the sample size to de-emphasize thin days by). */
export type EngineLatencyDay = {
    day: string
    decisionToDispatchMs: number | null
    dispatchToFillMs: number | null
    orders: number
}

/** `GET /api/admin/engine/metrics/execution` — the live HalftimeSwing
 *  baseline for the window. `outcomes.netCents` is real money:
 *  ProfitCents + AdjustmentCents over every resolved instance; wins/losses
 *  classify only Settled rows. */
export type EngineExecutionMetrics = {
    funnel: {
        /** Instances (for games scheduled in the window) that left
         *  `Configured`. */
        armed: number
        /** QualifyEntry decision rows. */
        qualified: number
        /** SkipEntry rows per verbatim machine-readable reason (`"unknown"`
         *  when a payload has none), descending by count then reason. */
        skippedByReason: Record<string, number>
        ordersPlaced: number
        ordersFilled: number
        ordersRejected: number
        /** Paper-routed entries (credentials-less) — placed, never fillable. */
        ordersPaper: number
        outcomes: {
            wins: number
            losses: number
            noTrade: number
            cancelled: number
            errored: number
            netCents: number
        }
    }
    latency: {
        decisionToDispatchMs: number | null
        dispatchToFillMs: number | null
        byDay: EngineLatencyDay[]
    }
    fillDelivery: {
        /** Per first-observer source ('ws' | 'sweep' | 'confirmer'), ordered
         *  by source name; silent sources are absent. `medianLagMs` is
         *  recorded − filled, 1 dp, raw (clock skew can go negative). */
        bySource: {
            source: string
            count: number
            contracts: number
            medianLagMs: number
        }[]
        /** Σ contracts of sweep-sourced fills — each one a fill WS lost. */
        sweepRecoveredContracts: number
    }
}

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public body?: unknown,
    ) {
        super(message)
    }
}

/**
 * The single authenticated-fetch choke point. Acquires the MSAL access token,
 * attaches it as a bearer credential, performs the request, and applies the
 * shared response policy — every non-2xx throws `ApiError` with the parsed body.
 * Returns the raw `Response` so callers consume it however they need (JSON,
 * blob, stream).
 *
 * Every request the app makes MUST flow through here so the bearer token is
 * never dropped — which is exactly why a file download can't be a bare
 * `<a href>`: a top-level browser navigation skips this path and reaches the
 * `[Authorize]` API unauthenticated (→ 401). Use `downloadRecording` instead.
 *
 * No token, no request. A call with the header missing is a guaranteed 401, so
 * it is refused locally instead of being spent on a round trip — that tokenless
 * `GET /api/user/me` was the visible half of ticket #33. When the reason is a
 * dead session, the session is retired here (`endSession`): the stale MSAL
 * account is evicted and `AuthProvider` routes to sign-in. Nothing navigates
 * from this layer; a background refetch must never yank the page.
 */
async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers)
    const token = await acquireApiToken()
    if (token.kind !== 'token') {
        if (token.kind === 'unavailable') {
            throw new ApiError(0, 'Access token unavailable')
        }
        if (token.kind === 'interaction-required') void endSession()
        throw new ApiError(401, 'Unauthorized')
    }
    headers.set('Authorization', `Bearer ${token.token}`)

    // Acting for an organization: name it on the request, then hold the server
    // to it below (ai_docs/corporate-user-feature.md §6.1). Personal requests
    // send nothing and assert nothing.
    const accountId = getActiveAccountId()
    if (accountId) headers.set(ACCOUNT_HEADER, accountId)

    const resp = await fetch(path, { ...init, headers })

    // A 401 on a request we DID sign means the backend rejected a live token —
    // the session is over regardless of what MSAL's cache still holds.
    if (resp.status === 401) {
        void endSession()
        throw new ApiError(401, 'Unauthorized')
    }

    if (!resp.ok) {
        const parsed = await parseJsonBody(resp)
        // The account can no longer be acted for (grant removed, delegation
        // switched off). The session itself is fine, so this never ends it;
        // the provider drops back to the personal account.
        if (accountId && resp.status === 403) {
            const reason = accessLostReason(parsed)
            if (reason) reportAccountAccessLost(accountId, reason)
        }
        throw new ApiError(resp.status, `HTTP ${resp.status}`, parsed)
    }

    // The echo is the server's word on whose data this is. A stale tab, a
    // proxy that dropped the header, or a bug above must never render or
    // spend one account's money under another's name.
    if (accountId && resp.headers.get(ACCOUNT_HEADER) !== accountId) {
        throw new ApiError(0, 'accountMismatch')
    }

    return resp
}

/** Request and response header naming the account a request acts for. */
export const ACCOUNT_HEADER = 'X-Tradeski-Account'

const parseJsonBody = async (resp: Response): Promise<unknown> => {
    try {
        return await resp.json()
    } catch {
        return undefined // body wasn't JSON
    }
}

/** The account-context middleware's 403 bodies — the only ones the fetch
 *  layer interprets. Any other 403 (a role gate) is the caller's business. */
const accessLostReason = (body: unknown): AccountAccessLostReason | null => {
    const error = (body as { error?: unknown } | undefined)?.error
    if (error === 'accountForbidden') return 'removed'
    if (error === 'delegationDisabled') return 'disabled'
    return null
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    const resp = await authorizedFetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    })

    if (resp.status === 204) return undefined as T
    return (await resp.json()) as T
}

/** Viewer's IANA time zone — the summary endpoint anchors its today/week PnL
 *  buckets at this zone's midnight instead of UTC midnight (ticket #15). */
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

/** Query string shared by both History endpoints: the server buckets days and
 *  "today" at the viewer's local midnight, so `tz` always rides along. */
const historyParams = (query: HistoryQuery): URLSearchParams => {
    const params = new URLSearchParams()
    if (query.from) params.set('from', query.from)
    if (query.to) params.set('to', query.to)
    if (query.algorithms && query.algorithms.length > 0)
        params.set('algorithms', query.algorithms.join(','))
    if (query.status) params.set('status', query.status)
    if (query.league) params.set('league', query.league)
    params.set('tz', browserTimeZone)
    return params
}

/** Query string for the engine metrics endpoints. `tz` rides along only where
 *  the server buckets by viewer-local day (overview) — blockers has no day
 *  series, so its route takes no tz (plan §6.1). */
const engineMetricsParams = (query: EngineMetricsQuery, withTz: boolean): URLSearchParams => {
    const params = new URLSearchParams()
    if (query.league) params.set('league', query.league)
    if (query.from) params.set('from', query.from)
    if (query.to) params.set('to', query.to)
    if (withTz) params.set('tz', browserTimeZone)
    return params
}

export const api = {
    // User
    getMe: async (): Promise<UserMe> => {
        const me = await request<UserMe>('GET', '/api/user/me')
        // A profile that no longer lists the account we are acting for is the
        // same access loss a 403 reports, just seen through the profile instead
        // of a rejection. Same channel, so the provider has one path to handle
        // and not two that must agree.
        const accountId = getActiveAccountId()
        if (accountId && !me.accounts.some((a) => a.id === accountId)) {
            reportAccountAccessLost(accountId, 'removed')
        }
        return me
    },
    getPreferences: () => request<UserPreferences>('GET', '/api/user/me/preferences'),
    setPreferences: (dashboardLayoutJson: string | null) =>
        request<void>('PUT', '/api/user/me/preferences', { dashboardLayoutJson }),

    // Credentials (self-service)
    getCredentialsStatus: () =>
        request<CredentialsStatus>('GET', '/api/user/me/credentials/status'),
    getKalshiBalance: () => request<UserBalance>('GET', '/api/user/me/balance'),
    setCredentials: (apiKeyId: string, privateKeyPem: string) =>
        request<void>('PUT', '/api/user/me/credentials', { apiKeyId, privateKeyPem }),
    deleteCredentials: () => request<void>('DELETE', '/api/user/me/credentials'),

    // Games
    /**
     * The full `{games, kalshiDirectoryStatus}` envelope. All UI consumers go
     * through `useGames()` (one shared cache entry, phase slices derived
     * client-side) — the server's `?phase=` filters remain for API consumers
     * but the frontend deliberately fetches the whole bounded board.
     */
    listGamesEnvelope: (phase?: 'live' | 'upcoming' | 'recent') => {
        const q = phase ? `?phase=${phase}` : ''
        return request<GameList>('GET', `/api/games${q}`)
    },
    getGame: (id: string) => request<Game>('GET', `/api/games/${id}`),
    linkGame: (id: string, kalshiMarketTickerOrUrl: string, yesSide: 'Home' | 'Away' | undefined) =>
        request<void>('PUT', `/api/games/${id}/link`, {
            kalshiMarketTickerOrUrl,
            ...(yesSide !== undefined ? { yesSide } : {}),
        }),
    autoLinkGame: (id: string, yesSide: 'Home' | 'Away' | undefined) =>
        request<AutoLinkResponse>('POST', `/api/games/${id}/auto-link`, {
            ...(yesSide !== undefined ? { yesSide } : {}),
        }),
    unlinkGame: (id: string) => request<void>('DELETE', `/api/games/${id}/link`),
    getGameMarketHistory: (id: string) =>
        request<GameMarketHistory>('GET', `/api/games/${id}/market-history`),

    // Algorithms
    listAlgorithms: () => request<Algorithm[]>('GET', '/api/algorithms'),
    quoteStake: (algorithmName: string, configJson: string) =>
        request<StakeQuote>('POST', `/api/algorithms/${encodeURIComponent(algorithmName)}/stake`, {
            configJson,
        }),

    // Instances
    listInstances: () => request<Instance[]>('GET', '/api/instances'),
    listActiveInstances: () => request<ActiveInstance[]>('GET', '/api/instances/active'),
    getInstanceMarketHistory: (id: string) =>
        request<MarketHistory>('GET', `/api/instances/${id}/market-history`),
    getInstance: (id: string) => request<Instance>('GET', `/api/instances/${id}`),
    /** Arm an instance. `confirmedStakeCents` is the stake the person accepted
     *  from a `StakeConfirmationRequired` refusal; only an exact match arms. */
    createInstance: (
        gameId: string,
        algorithmName: string,
        configJson: string,
        confirmedStakeCents?: number,
    ) =>
        request<Instance>('POST', '/api/instances', {
            gameId,
            algorithmName,
            configJson,
            ...(confirmedStakeCents !== undefined ? { confirmedStakeCents } : {}),
        }),
    cancelInstance: (id: string) => request<void>('POST', `/api/instances/${id}/cancel`),
    getSlate: (algorithmName: string) =>
        request<Slate>(
            'GET',
            `/api/instances/slate?algorithm=${encodeURIComponent(algorithmName)}`,
        ),
    /** Arm one configuration on the chosen games of tonight's slate. A stake
     *  that needs confirming refuses the whole slate (409,
     *  `SlateStakeConfirmationRequired`) until `confirmedStakeCents` repeats
     *  the per-game stake exactly. */
    armSlate: (
        algorithmName: string,
        configJson: string,
        gameIds: string[],
        confirmed?: { stakeCents: number; games: number },
    ) =>
        request<ArmSlateResponse>('POST', '/api/instances/slate', {
            algorithmName,
            configJson,
            gameIds,
            ...(confirmed !== undefined
                ? { confirmedStakeCents: confirmed.stakeCents, confirmedGameCount: confirmed.games }
                : {}),
        }),
    /** Sell the instance's whole open position into the live bid now.
     *  `quotedMarkCents` is the bid the operator was looking at: the server
     *  turns it into a floor the exit may never sell below and refuses
     *  outright if the market has already fallen through it, so a stale quote
     *  can never spend money at a price nobody saw. A refusal arrives as an
     *  `ApiError` (409) whose `body` is a `SellNowResponse`. */
    sellNowInstance: (id: string, quotedMarkCents: number) =>
        request<SellNowResponse>('POST', `/api/instances/${id}/sell-now`, {
            quotedMarkCents,
        }),

    // Dashboard
    getDashboardLayout: () => request<DashboardLayout>('GET', '/api/dashboard/layout'),
    setDashboardLayout: (layoutJson: string | null) =>
        request<void>('PUT', '/api/dashboard/layout', { layoutJson }),
    getDashboardSummary: () =>
        request<DashboardSummary>(
            'GET',
            `/api/dashboard/summary?tz=${encodeURIComponent(browserTimeZone)}`,
        ),

    // History
    historySummary: (query: HistoryQuery) =>
        request<HistorySummary>('GET', `/api/history/summary?${historyParams(query)}`),
    historyTrades: (query: HistoryQuery & { limit?: number; before?: string }) => {
        const params = historyParams(query)
        if (query.limit) params.set('limit', String(query.limit))
        if (query.before) params.set('before', query.before)
        return request<HistoryTrade[]>('GET', `/api/history/trades?${params}`)
    },
    historyTrade: (instanceId: string) =>
        request<HistoryTradeDetail>('GET', `/api/history/trades/${instanceId}`),

    // Admin
    adminListUsers: () => request<AdminUserSummary[]>('GET', '/api/admin/users'),
    adminCreateUser: (req: CreateAccountRequest) =>
        request<AdminUserSummary>('POST', '/api/admin/users', req),
    adminSetCommission: (userId: string, commissionRate: number | null) =>
        request<void>('PUT', `/api/admin/users/${userId}/commission`, { commissionRate }),
    adminRevokeCredentials: (userId: string) =>
        request<void>('DELETE', `/api/admin/users/${userId}/credentials`),

    // Who may act for an organization (corporate-user-feature §7.2). Both
    // writes are idempotent server-side; a revoke also evicts the person's
    // live delegated hub connections.
    adminListAccountGrants: (accountId: string) =>
        request<AccountGrantee[]>('GET', `/api/admin/accounts/${accountId}/grants`),
    adminGrantAccount: (accountId: string, userId: string) =>
        request<void>('PUT', `/api/admin/accounts/${accountId}/grants/${userId}`),
    adminRevokeAccountGrant: (accountId: string, userId: string) =>
        request<void>('DELETE', `/api/admin/accounts/${accountId}/grants/${userId}`),

    adminListCommissions: (status?: 'Pending' | 'Paid' | 'Waived' | 'None') => {
        const q = status ? `?status=${status}` : ''
        return request<CommissionEntry[]>('GET', `/api/admin/commissions${q}`)
    },
    adminMarkCommissionPaid: (id: string, notes?: string) =>
        request<void>('POST', `/api/admin/commissions/${id}/mark-paid`, { notes: notes ?? null }),
    adminWaiveCommission: (id: string, notes: string) =>
        request<void>('POST', `/api/admin/commissions/${id}/waive`, { notes }),
    adminAdjustCommission: (id: string, deltaCents: number, reason: string) =>
        request<void>('POST', `/api/admin/commissions/${id}/adjust`, { deltaCents, reason }),
    adminGetCommissionReport: (id: string) =>
        request<CommissionReport>('GET', `/api/admin/commissions/${id}/report`),

    adminListAudit: (opts?: {
        action?: string
        actorUserId?: string
        /** Rows written while acting for this organization. */
        accountUserId?: string
        target?: string
        search?: string
        /** ISO instant; server filters `At >= from`. */
        from?: string
        /** ISO instant; server filters `At < to` (exclusive upper bound). */
        to?: string
        limit?: number
        /** Keyset cursor — pass the last row's id to fetch the next, older page. */
        beforeId?: number
    }) => {
        const params = new URLSearchParams()
        if (opts?.action) params.set('action', opts.action)
        if (opts?.actorUserId) params.set('actorUserId', opts.actorUserId)
        if (opts?.accountUserId) params.set('accountUserId', opts.accountUserId)
        if (opts?.target) params.set('target', opts.target)
        if (opts?.search) params.set('search', opts.search)
        if (opts?.from) params.set('from', opts.from)
        if (opts?.to) params.set('to', opts.to)
        if (opts?.limit) params.set('limit', String(opts.limit))
        if (opts?.beforeId) params.set('beforeId', String(opts.beforeId))
        const q = params.toString()
        return request<AuditEntry[]>('GET', `/api/admin/audit${q ? '?' + q : ''}`)
    },
    /** Distinct action verbs present in the log — backs the filter dropdown. */
    adminListAuditActions: () => request<string[]>('GET', '/api/admin/audit/actions'),

    adminListSettings: () => request<SystemSetting[]>('GET', '/api/admin/settings'),
    adminSetSetting: (key: string, valueJson: string) =>
        request<void>('PUT', `/api/admin/settings/${encodeURIComponent(key)}`, { valueJson }),

    // Recordings — an admin-only operator surface (server enforces the role;
    // see AdminRecordingsController). Regular users have no recordings API.
    adminListRecordings: (page = 1, pageSize = 15) =>
        request<PagedRecordings>('GET', `/api/admin/recordings?page=${page}&pageSize=${pageSize}`),
    adminListRecordingRequests: () =>
        request<RecordingRequest[]>('GET', '/api/admin/recordings/requests'),
    adminStartCapture: (gameId: string) =>
        request<RecordingRequest>('POST', `/api/admin/recordings/${gameId}/capture`),
    adminStopCapture: (gameId: string) =>
        request<void>('DELETE', `/api/admin/recordings/${gameId}/capture`),
    adminAlwaysRecord: (gameId: string) =>
        request<RecordingRequest>('POST', `/api/admin/recordings/${gameId}`),
    adminDeleteRecording: (gameId: string, force = false) =>
        request<void>('DELETE', `/api/admin/recordings/${gameId}${force ? '?force=true' : ''}`),

    // Predictive engine — analyses are system-level, readable by any
    // authenticated user; admin surfaces are role-gated server-side.
    listEngineAnalyses: (limit = 20) =>
        request<EngineAnalysisSummary[]>('GET', `/api/engine/analyses?limit=${limit}`),
    getEngineAnalysis: (analysisId: string) =>
        request<EngineAnalysisDetail>('GET', `/api/engine/analyses/${analysisId}`),

    adminEngineStatus: () => request<EngineStatus>('GET', '/api/admin/engine/status'),
    adminEngineObservations: (
        league: League,
        opts?: { limit?: number; before?: number; beforeId?: string },
    ) => {
        const params = new URLSearchParams({ league })
        if (opts?.limit) params.set('limit', String(opts.limit))
        if (opts?.before !== undefined) params.set('before', String(opts.before))
        if (opts?.beforeId) params.set('beforeId', opts.beforeId)
        return request<EngineObservation[]>('GET', `/api/admin/engine/observations?${params}`)
    },
    adminEngineRules: (league?: League) =>
        request<EngineRule[]>('GET', `/api/admin/engine/rules${league ? `?league=${league}` : ''}`),
    adminEngineApproveRule: (ruleId: string, notes: string) =>
        request<void>('POST', `/api/admin/engine/rules/${ruleId}/approve`, { notes }),
    adminEngineRetireRule: (ruleId: string, notes: string) =>
        request<void>('POST', `/api/admin/engine/rules/${ruleId}/retire`, { notes }),
    adminEngineBackfill: (gameId: string) =>
        request<EngineBackfillJob>('POST', '/api/admin/engine/backfill', { gameId }),
    adminEngineBackfillJob: (id: string) =>
        request<EngineBackfillJob>('GET', `/api/admin/engine/backfill/${id}`),
    // Raw NDJSON body (one labeled-observation row per line — the
    // engine-corpus export contract), so this drives `authorizedFetch`
    // directly instead of the JSON `request` helper.
    adminEngineImportObservations: async (ndjson: string, dryRun: boolean) => {
        const resp = await authorizedFetch(
            `/api/admin/engine/observations/import?dryRun=${dryRun}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-ndjson' },
                body: ndjson,
            },
        )
        return (await resp.json()) as EngineObservationImportReport
    },
    adminEngineMineRules: (league: League) =>
        request<EngineMiningJob>('POST', '/api/admin/engine/rules/mine', { league }),
    adminEngineMiningJob: (id: string) =>
        request<EngineMiningJob>('GET', `/api/admin/engine/rules/mine/${id}`),
    adminEngineMetricsOverview: (query: EngineMetricsQuery = {}) =>
        request<EngineMetricsOverview>(
            'GET',
            `/api/admin/engine/metrics/overview?${engineMetricsParams(query, true)}`,
        ),
    adminEngineMetricsBlockers: (query: EngineMetricsQuery = {}) =>
        request<EngineBlockerMetrics>(
            'GET',
            `/api/admin/engine/metrics/blockers?${engineMetricsParams(query, false)}`,
        ),
    adminEngineMetricsLedger: (query: EngineMetricsQuery = {}) =>
        request<EngineLedgerMetrics>(
            'GET',
            `/api/admin/engine/metrics/ledger?${engineMetricsParams(query, true)}`,
        ),
    adminEngineMetricsExecution: (query: EngineMetricsQuery = {}) =>
        request<EngineExecutionMetrics>(
            'GET',
            `/api/admin/engine/metrics/execution?${engineMetricsParams(query, true)}`,
        ),
    // No `tz` — calibration has no day series (the blockers posture).
    adminEngineMetricsCalibration: (
        metric: EngineCalibrationMetric,
        query: EngineMetricsQuery = {},
    ) => {
        const params = engineMetricsParams(query, false)
        params.set('metric', metric)
        return request<EngineCalibrationMetrics>(
            'GET',
            `/api/admin/engine/metrics/calibration?${params}`,
        )
    },
    // Engine-board layout (engine-metrics plan §3.4) — the dashboard-layout
    // contract on the admin engine slot: effective layout + source, PUT null
    // clears the override.
    adminEngineLayout: () => request<DashboardLayout>('GET', '/api/admin/engine/layout'),
    adminEngineSetLayout: (layoutJson: string | null) =>
        request<void>('PUT', '/api/admin/engine/layout', { layoutJson }),

    // Phase 4: replay — admin-only (server enforces the role; see AdminReplayController)
    replayListGames: () => request<Game[]>('GET', '/api/admin/replay/games'),
    replayCreateSession: (
        gameId: string,
        algorithmName: string,
        configJson: string | null,
        speed: ReplaySpeed,
    ) =>
        request<ReplaySession>('POST', '/api/admin/replay/sessions', {
            gameId,
            algorithmName,
            configJson,
            speed,
        }),
    replayGetSession: (id: string) =>
        request<ReplaySession>('GET', `/api/admin/replay/sessions/${id}`),
    replayGetSessionDetail: (id: string) =>
        request<ReplaySessionDetail>('GET', `/api/admin/replay/sessions/${id}/detail`),
    replayListSessions: () => request<ReplaySession[]>('GET', '/api/admin/replay/sessions'),
    replayCancelSession: (id: string) =>
        request<void>('DELETE', `/api/admin/replay/sessions/${id}`),
    replayBacktest: (algorithmName: string, configJson: string | null, gameIds: string[]) =>
        request<BacktestJob>('POST', '/api/admin/replay/backtest', {
            algorithmName,
            configJson,
            gameIds,
        }),
    replayGetBacktest: (jobId: string) =>
        request<BacktestJob>('GET', `/api/admin/replay/backtest/${jobId}`),
}

/**
 * Multipart-upload helper for admin archive imports. `request` is JSON-only, so
 * this drives `authorizedFetch` directly with a `FormData` body (whose
 * multipart Content-Type + boundary the browser must set itself).
 */
export async function uploadAdminRecording(file: File): Promise<RecordingImportResult[]> {
    const form = new FormData()
    form.append('file', file)
    const resp = await authorizedFetch('/api/admin/recordings/import', {
        method: 'POST',
        body: form,
    })
    return (await resp.json()) as RecordingImportResult[]
}

/**
 * Authenticated download of a recording's ZIP archive. A plain `<a href>` can't
 * be used: a top-level browser navigation doesn't carry the MSAL bearer token,
 * so the `[Authorize]` export endpoint rejects it with 401. Instead we fetch
 * the archive through `authorizedFetch` (token attached) and hand the blob to a
 * transient object-URL anchor to trigger the browser's save dialog. The
 * filename comes from the server's Content-Disposition
 * (`tradeski-recording-<espnEventId>.zip`), with a gameId-based fallback.
 */
export async function downloadRecording(gameId: string): Promise<void> {
    const resp = await authorizedFetch(`/api/admin/recordings/${gameId}/export`)
    const blob = await resp.blob()
    const filename =
        filenameFromContentDisposition(resp.headers.get('Content-Disposition')) ??
        `tradeski-recording-${gameId}.zip`
    saveBlob(blob, filename)
}

/**
 * Authenticated download of the History CSV export for a filter set — same
 * blob-anchor route as `downloadRecording` (a bare `<a href>` would navigate
 * without the bearer token). The server names the file after the filtered
 * range (`tradeski-history-<range>.csv`) via Content-Disposition.
 */
/** Saves `GET /api/instances/evaluations` — one algorithm's instances whose
 *  game falls in `[from, to)`, each with the decision log that says why it
 *  did or did not trade — verbatim as JSON: the live side of the research
 *  parity check (tools/nba-edge-research/fallen_favorite.py parity). */
export async function downloadEvaluations(
    algorithmName: string,
    from: string,
    to: string,
): Promise<void> {
    const params = new URLSearchParams({ algorithm: algorithmName, from, to })
    const resp = await authorizedFetch(`/api/instances/evaluations?${params}`)
    saveBlob(
        await resp.blob(),
        `${algorithmName}-evaluations-${from.slice(0, 10)}-${to.slice(0, 10)}.json`,
    )
}

export async function downloadHistoryExport(query: HistoryQuery): Promise<void> {
    const resp = await authorizedFetch(`/api/history/export?${historyParams(query)}`)
    const blob = await resp.blob()
    const filename =
        filenameFromContentDisposition(resp.headers.get('Content-Disposition')) ??
        'tradeski-history.csv'
    saveBlob(blob, filename)
}

/** Extracts the `filename` (or RFC 5987 `filename*`) from a Content-Disposition
 *  header. Returns null when absent/unparseable so callers can fall back. */
function filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null
    const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i)
    if (utf8) return decodeURIComponent(utf8[1])
    const ascii = header.match(/filename="?([^";]+)"?/i)
    return ascii ? ascii[1] : null
}

/** Saves a blob to disk via a transient object-URL anchor, then revokes the
 *  URL. Reusable by any future authenticated download. */
function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    // Defer the revoke: some browsers cancel an in-flight download if the
    // object URL is freed in the same tick as the click.
    setTimeout(() => URL.revokeObjectURL(url), 0)
}
